import {Injectable} from '@angular/core';
import {AvailableResult, BiometryType, Credentials, NativeBiometric} from '@capgo/capacitor-native-biometric';
import {TranslocoService} from '@ngneat/transloco';
import {StorageService} from '../storage/storage.service';
import {BiometricInfo} from '../../models/biometricInfo.model';
import {Client} from '@mlc/types';
import {ToastController} from "@ionic/angular";
import {SilentErrorsEnum} from "../../models/enums/SilentErrors.enum";
import * as SentryAngular from "@sentry/angular";
import { Capacitor } from '@capacitor/core';
import { PlatformEnum } from '../../models/enums/platform.enum';

@Injectable({
	providedIn: 'root'
})
export class BiometricService {

	public biometricCredential: Credentials | null = null;

	constructor(
		private transloco: TranslocoService,
		private storage: StorageService,
    private toastService: ToastController
	) {
	}

	async loginWithBiometrics(): Promise<{ username: string; password: string } | null> {
    const result: AvailableResult | null = await this.biometricIsAvailable();
    if (!(result?.isAvailable || (!result?.isAvailable && result?.errorCode === 1 && result?.biometryType === BiometryType.NONE))) return null

		const isFaceID: boolean = [BiometryType.FACE_ID, BiometryType.NONE].includes(result?.biometryType);

		this.biometricCredential = await NativeBiometric.verifyIdentity({
			reason: this.transloco.translate('AUTH.BIOMETRIC.REASON'),
			title: this.transloco.translate('AUTH.BIOMETRIC.TITLE'),
			subtitle: isFaceID ? this.transloco.translate('AUTH.BIOMETRIC.SUBTITLE_FACE_ID') : this.transloco.translate('AUTH.BIOMETRIC.SUBTITLE_FINGERPRINT'),
			description: '',
			negativeButtonText: this.transloco.translate('AUTH.BIOMETRIC.BACK'),
			useFallback: true
		}).then(() => this.getCredentials()).catch((error: Error) => {
      this.toastService.create({
        message: this.transloco.translate(error.message),
        swipeGesture: 'vertical',
        position: 'bottom',
        color: 'danger',
	      duration: 6000,
      }).then(alert => alert.present());
			if (error?.message?.includes(SilentErrorsEnum.AUTH_CANCELLED)) {
				return null;
			}
      console.error(
        error
      )
      return null;
    });
		return this.biometricCredential;
	}

	async getCredentials(): Promise<Credentials | null> {
		if (Capacitor.getPlatform() === PlatformEnum.WEB) {
			return null;
		}
		return NativeBiometric.getCredentials({
			server: 'com.monlivretc.auth',
		}).catch((e) => {
			console.error('Error :', e)
			return null;
		});
	}

	async saveCredentials(username: string, password: string): Promise<void> {
		const result: AvailableResult | null = await this.biometricIsAvailable();
		if (!result?.isAvailable) {
			return;
		}

		await NativeBiometric.setCredentials({
			username,
			password,
			server: 'com.monlivretc.auth',
		});
	}

	async deleteCredentials(): Promise<void> {
		await NativeBiometric.deleteCredentials({
			server: 'com.monlivretc.auth',
		}).catch(() => {
		});
	}

	async biometricIsAvailable(): Promise<AvailableResult | null> {
		return await NativeBiometric.isAvailable().catch(() => null);
	}

	async setBiometricInfo(user: Client): Promise<void> {
		const result: AvailableResult | null = await this.biometricIsAvailable();
		if (!result?.isAvailable) {
			return;
		}
		const biometricInfo: BiometricInfo = {
			firstname: user.firstName,
			lastname: user.lastName,
			profilePicture: user.profilePicture?.url,
			biometricType: result.biometryType
		}

		return this.storage.set("biometricInfo", biometricInfo)
	}

	getBiometricInfo(): Promise<BiometricInfo> {
		return this.storage.get("biometricInfo").then((biometricInfo: BiometricInfo) => {
			const scope = SentryAngular.getCurrentScope();
			scope.setUser({
				username: `${biometricInfo.firstname} ${biometricInfo.lastname}`
			});
			return biometricInfo
		});
	}

	removeBiometricInfo(): Promise<void> {
		return this.storage.remove("biometricInfo");
	}
}
